import * as React from 'react';
import { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  Template,
  TemplateState,
  TemplateAction,
  TemplateBlock,
  TemplatePart,
  MergeField,
  TemplateHistoryEntry,
  CollaboratorInfo,
  VersionHistoryEntry,
  CollaborationSession,
  ApprovalWorkflow,
  Comment,
  ConflictDetail
} from '../types/template';
import { generateId } from '../utils/templateUtils';

// Initial state
const initialState: TemplateState = {
  currentTemplate: null,
  templates: [],
  templateParts: [],
  mergeFields: [],
  selectedBlockId: null,
  isEditing: false,
  isDirty: false,
  history: [],
  historyIndex: -1,
  collaborators: [],
  loading: false,
  error: null,
  // New collaboration features
  versionHistory: [],
  collaborationSessions: [],
  approvalWorkflows: [],
  comments: [],
  conflictResolution: undefined,
};

// Template reducer
function templateReducer(state: TemplateState, action: TemplateAction): TemplateState {
  switch (action.type) {
    case 'SET_TEMPLATES':
      return {
        ...state,
        templates: action.payload,
        loading: false,
        error: null,
      };

    case 'SET_CURRENT_TEMPLATE':
      return {
        ...state,
        currentTemplate: action.payload,
        selectedBlockId: null,
        isDirty: false,
        loading: false,
        error: null,
      };

    case 'UPDATE_TEMPLATE':
      if (!state.currentTemplate) return state;
      
      const updatedTemplate = {
        ...state.currentTemplate,
        ...action.payload,
        metadata: {
          ...state.currentTemplate.metadata,
          ...action.payload.metadata,
          updatedAt: new Date(),
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        isDirty: true,
      };

    case 'ADD_BLOCK': {
      if (!state.currentTemplate) return state;

      const newBlock = {
        ...action.payload.block,
        id: action.payload.block.id || generateId(),
      };

      const updatedContent = [...state.currentTemplate.content];
      
      if (action.payload.parentId) {
        // Add as child block (nested structure)
        const parentIndex = updatedContent.findIndex(block => block.id === action.payload.parentId);
        if (parentIndex !== -1) {
          newBlock.position.parent = action.payload.parentId;
        }
      }

      if (typeof action.payload.index === 'number') {
        updatedContent.splice(action.payload.index, 0, newBlock);
      } else {
        updatedContent.push(newBlock);
      }

      // Reorder blocks
      updatedContent.forEach((block, index) => {
        block.position.order = index;
      });

      const updatedTemplate = {
        ...state.currentTemplate,
        content: updatedContent,
        metadata: {
          ...state.currentTemplate.metadata,
          updatedAt: new Date(),
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        selectedBlockId: newBlock.id,
        isDirty: true,
      };
    }

    case 'UPDATE_BLOCK': {
      if (!state.currentTemplate) return state;

      const updatedContent = state.currentTemplate.content.map(block =>
        block.id === action.payload.blockId
          ? { ...block, ...action.payload.updates }
          : block
      );

      const updatedTemplate = {
        ...state.currentTemplate,
        content: updatedContent,
        metadata: {
          ...state.currentTemplate.metadata,
          updatedAt: new Date(),
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        isDirty: true,
      };
    }

    case 'DELETE_BLOCK': {
      if (!state.currentTemplate) return state;

      const updatedContent = state.currentTemplate.content
        .filter(block => block.id !== action.payload)
        .map((block, index) => ({
          ...block,
          position: { ...block.position, order: index },
        }));

      const updatedTemplate = {
        ...state.currentTemplate,
        content: updatedContent,
        metadata: {
          ...state.currentTemplate.metadata,
          updatedAt: new Date(),
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        selectedBlockId: state.selectedBlockId === action.payload ? null : state.selectedBlockId,
        isDirty: true,
      };
    }

    case 'SELECT_BLOCK':
      return {
        ...state,
        selectedBlockId: action.payload,
      };

    case 'MOVE_BLOCK': {
      if (!state.currentTemplate) return state;

      const { blockId, newIndex, newParentId } = action.payload;
      const updatedContent = [...state.currentTemplate.content];
      const blockIndex = updatedContent.findIndex(block => block.id === blockId);
      
      if (blockIndex === -1) return state;

      const [movedBlock] = updatedContent.splice(blockIndex, 1);
      movedBlock.position.parent = newParentId;
      updatedContent.splice(newIndex, 0, movedBlock);

      // Reorder all blocks
      updatedContent.forEach((block, index) => {
        block.position.order = index;
      });

      const updatedTemplate = {
        ...state.currentTemplate,
        content: updatedContent,
        metadata: {
          ...state.currentTemplate.metadata,
          updatedAt: new Date(),
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        isDirty: true,
      };
    }

    case 'SET_TEMPLATE_PARTS':
      return {
        ...state,
        templateParts: action.payload,
      };

    case 'ADD_TEMPLATE_PART':
      return {
        ...state,
        templateParts: [...state.templateParts, action.payload],
      };

    case 'UPDATE_TEMPLATE_PART':
      return {
        ...state,
        templateParts: state.templateParts.map(part =>
          part.id === action.payload.id
            ? { ...part, ...action.payload.updates }
            : part
        ),
      };

    case 'SET_MERGE_FIELDS':
      return {
        ...state,
        mergeFields: action.payload,
      };

    case 'ADD_MERGE_FIELD':
      return {
        ...state,
        mergeFields: [...state.mergeFields, action.payload],
      };

    case 'UPDATE_MERGE_FIELD':
      return {
        ...state,
        mergeFields: state.mergeFields.map(field =>
          field.id === action.payload.id
            ? { ...field, ...action.payload.updates }
            : field
        ),
      };

    case 'SET_EDITING':
      return {
        ...state,
        isEditing: action.payload,
      };

    case 'SET_DIRTY':
      return {
        ...state,
        isDirty: action.payload,
      };

    case 'ADD_HISTORY_ENTRY': {
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      newHistory.push(action.payload);
      
      // Limit history to 50 entries
      if (newHistory.length > 50) {
        newHistory.shift();
      }

      return {
        ...state,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      };
    }

    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      
      const previousEntry = state.history[state.historyIndex - 1];
      return {
        ...state,
        currentTemplate: previousEntry.templateSnapshot,
        historyIndex: state.historyIndex - 1,
        isDirty: true,
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      
      const nextEntry = state.history[state.historyIndex + 1];
      return {
        ...state,
        currentTemplate: nextEntry.templateSnapshot,
        historyIndex: state.historyIndex + 1,
        isDirty: true,
      };
    }

    case 'SET_COLLABORATORS':
      return {
        ...state,
        collaborators: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case 'RESET_STATE':
      return initialState;

    // New collaboration cases
    case 'SET_VERSION_HISTORY':
      return {
        ...state,
        versionHistory: action.payload,
      };

    case 'ADD_VERSION_HISTORY_ENTRY':
      return {
        ...state,
        versionHistory: [...state.versionHistory, action.payload].sort((a, b) => 
          b.createdAt.getTime() - a.createdAt.getTime()
        ),
      };

    case 'RESTORE_VERSION': {
      const { templateSnapshot } = action.payload;
      return {
        ...state,
        currentTemplate: templateSnapshot,
        isDirty: true,
        selectedBlockId: null,
      };
    }

    case 'SET_COLLABORATION_SESSIONS':
      return {
        ...state,
        collaborationSessions: action.payload,
      };

    case 'UPDATE_COLLABORATION_SESSION': {
      const { sessionId, updates } = action.payload;
      return {
        ...state,
        collaborationSessions: state.collaborationSessions.map(session =>
          session.id === sessionId
            ? { ...session, ...updates, lastActivity: new Date() }
            : session
        ),
      };
    }

    case 'REMOVE_COLLABORATION_SESSION':
      return {
        ...state,
        collaborationSessions: state.collaborationSessions.filter(
          session => session.id !== action.payload
        ),
      };

    case 'SET_COMMENTS':
      return {
        ...state,
        comments: action.payload,
      };

    case 'ADD_COMMENT':
      return {
        ...state,
        comments: [...state.comments, action.payload],
      };

    case 'UPDATE_COMMENT': {
      const { commentId, updates } = action.payload;
      return {
        ...state,
        comments: state.comments.map(comment =>
          comment.id === commentId
            ? { ...comment, ...updates, updatedAt: new Date() }
            : comment
        ),
      };
    }

    case 'DELETE_COMMENT':
      return {
        ...state,
        comments: state.comments.filter(comment => comment.id !== action.payload),
      };

    case 'RESOLVE_COMMENT': {
      const { commentId, resolvedBy } = action.payload;
      return {
        ...state,
        comments: state.comments.map(comment =>
          comment.id === commentId
            ? { 
                ...comment, 
                resolved: true, 
                resolvedBy, 
                resolvedAt: new Date() 
              }
            : comment
        ),
      };
    }

    case 'SET_APPROVAL_WORKFLOWS':
      return {
        ...state,
        approvalWorkflows: action.payload,
      };

    case 'ADD_APPROVAL_WORKFLOW':
      return {
        ...state,
        approvalWorkflows: [...state.approvalWorkflows, action.payload],
      };

    case 'UPDATE_APPROVAL_WORKFLOW': {
      const { workflowId, updates } = action.payload;
      return {
        ...state,
        approvalWorkflows: state.approvalWorkflows.map(workflow =>
          workflow.id === workflowId
            ? { ...workflow, ...updates }
            : workflow
        ),
      };
    }

    case 'SET_CONFLICTS': {
      const { hasConflicts, conflictingBlocks, conflictDetails } = action.payload;
      return {
        ...state,
        conflictResolution: {
          hasConflicts,
          conflictingBlocks,
          conflictDetails,
        },
      };
    }

    case 'RESOLVE_CONFLICT': {
      if (!state.conflictResolution) return state;
      
      const { blockId } = action.payload;
      const updatedConflictingBlocks = state.conflictResolution.conflictingBlocks.filter(
        id => id !== blockId
      );
      const updatedConflictDetails = state.conflictResolution.conflictDetails.filter(
        detail => detail.blockId !== blockId
      );

      return {
        ...state,
        conflictResolution: {
          hasConflicts: updatedConflictingBlocks.length > 0,
          conflictingBlocks: updatedConflictingBlocks,
          conflictDetails: updatedConflictDetails,
        },
      };
    }

    case 'UPDATE_PERMISSIONS': {
      if (!state.currentTemplate) return state;
      
      const updatedTemplate = {
        ...state.currentTemplate,
        collaboration: {
          ...state.currentTemplate.collaboration,
          permissions: action.payload.permissions,
        },
      };

      return {
        ...state,
        currentTemplate: updatedTemplate,
        isDirty: true,
      };
    }

    default:
      return state;
  }
}

// Context
const TemplateContext = createContext<{
  state: TemplateState;
  dispatch: React.Dispatch<TemplateAction>;
  actions: TemplateActions;
} | null>(null);

// Action creators
interface TemplateActions {
  setTemplates: (templates: Template[]) => void;
  setCurrentTemplate: (template: Template | null) => void;
  updateTemplate: (updates: Partial<Template>) => void;
  addBlock: (block: TemplateBlock, parentId?: string, index?: number) => void;
  updateBlock: (blockId: string, updates: Partial<TemplateBlock>) => void;
  deleteBlock: (blockId: string) => void;
  selectBlock: (blockId: string | null) => void;
  moveBlock: (blockId: string, newIndex: number, newParentId?: string) => void;
  setTemplateParts: (parts: TemplatePart[]) => void;
  addTemplatePart: (part: TemplatePart) => void;
  updateTemplatePart: (id: string, updates: Partial<TemplatePart>) => void;
  setMergeFields: (fields: MergeField[]) => void;
  addMergeField: (field: MergeField) => void;
  updateMergeField: (id: string, updates: Partial<MergeField>) => void;
  setEditing: (editing: boolean) => void;
  setDirty: (dirty: boolean) => void;
  undo: () => void;
  redo: () => void;
  addHistoryEntry: (entry: TemplateHistoryEntry) => void;
  setCollaborators: (collaborators: CollaboratorInfo[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetState: () => void;
  // New collaboration actions
  setVersionHistory: (versions: VersionHistoryEntry[]) => void;
  addVersionHistoryEntry: (version: VersionHistoryEntry) => void;
  restoreVersion: (versionId: string, templateSnapshot: Template) => void;
  setCollaborationSessions: (sessions: CollaborationSession[]) => void;
  updateCollaborationSession: (sessionId: string, updates: Partial<CollaborationSession>) => void;
  removeCollaborationSession: (sessionId: string) => void;
  setComments: (comments: Comment[]) => void;
  addComment: (comment: Comment) => void;
  updateComment: (commentId: string, updates: Partial<Comment>) => void;
  deleteComment: (commentId: string) => void;
  resolveComment: (commentId: string, resolvedBy: string) => void;
  setApprovalWorkflows: (workflows: ApprovalWorkflow[]) => void;
  addApprovalWorkflow: (workflow: ApprovalWorkflow) => void;
  updateApprovalWorkflow: (workflowId: string, updates: Partial<ApprovalWorkflow>) => void;
  setConflicts: (hasConflicts: boolean, conflictingBlocks: string[], conflictDetails: ConflictDetail[]) => void;
  resolveConflict: (blockId: string, resolution: 'local' | 'remote' | 'merge') => void;
  updatePermissions: (templateId: string, permissions: any[]) => void;
}

// Provider component
export const TemplateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(templateReducer, initialState);

  // Action creators
  const actions: TemplateActions = {
    setTemplates: useCallback((templates: Template[]) => {
      dispatch({ type: 'SET_TEMPLATES', payload: templates });
    }, []),

    setCurrentTemplate: useCallback((template: Template | null) => {
      dispatch({ type: 'SET_CURRENT_TEMPLATE', payload: template });
    }, []),

    updateTemplate: useCallback((updates: Partial<Template>) => {
      dispatch({ type: 'UPDATE_TEMPLATE', payload: updates });
    }, []),

    addBlock: useCallback((block: TemplateBlock, parentId?: string, index?: number) => {
      dispatch({ type: 'ADD_BLOCK', payload: { block, parentId, index } });
    }, []),

    updateBlock: useCallback((blockId: string, updates: Partial<TemplateBlock>) => {
      dispatch({ type: 'UPDATE_BLOCK', payload: { blockId, updates } });
    }, []),

    deleteBlock: useCallback((blockId: string) => {
      dispatch({ type: 'DELETE_BLOCK', payload: blockId });
    }, []),

    selectBlock: useCallback((blockId: string | null) => {
      dispatch({ type: 'SELECT_BLOCK', payload: blockId });
    }, []),

    moveBlock: useCallback((blockId: string, newIndex: number, newParentId?: string) => {
      dispatch({ type: 'MOVE_BLOCK', payload: { blockId, newIndex, newParentId } });
    }, []),

    setTemplateParts: useCallback((parts: TemplatePart[]) => {
      dispatch({ type: 'SET_TEMPLATE_PARTS', payload: parts });
    }, []),

    addTemplatePart: useCallback((part: TemplatePart) => {
      dispatch({ type: 'ADD_TEMPLATE_PART', payload: part });
    }, []),

    updateTemplatePart: useCallback((id: string, updates: Partial<TemplatePart>) => {
      dispatch({ type: 'UPDATE_TEMPLATE_PART', payload: { id, updates } });
    }, []),

    setMergeFields: useCallback((fields: MergeField[]) => {
      dispatch({ type: 'SET_MERGE_FIELDS', payload: fields });
    }, []),

    addMergeField: useCallback((field: MergeField) => {
      dispatch({ type: 'ADD_MERGE_FIELD', payload: field });
    }, []),

    updateMergeField: useCallback((id: string, updates: Partial<MergeField>) => {
      dispatch({ type: 'UPDATE_MERGE_FIELD', payload: { id, updates } });
    }, []),

    setEditing: useCallback((editing: boolean) => {
      dispatch({ type: 'SET_EDITING', payload: editing });
    }, []),

    setDirty: useCallback((dirty: boolean) => {
      dispatch({ type: 'SET_DIRTY', payload: dirty });
    }, []),

    undo: useCallback(() => {
      dispatch({ type: 'UNDO' });
    }, []),

    redo: useCallback(() => {
      dispatch({ type: 'REDO' });
    }, []),

    addHistoryEntry: useCallback((entry: TemplateHistoryEntry) => {
      dispatch({ type: 'ADD_HISTORY_ENTRY', payload: entry });
    }, []),

    setCollaborators: useCallback((collaborators: CollaboratorInfo[]) => {
      dispatch({ type: 'SET_COLLABORATORS', payload: collaborators });
    }, []),

    setLoading: useCallback((loading: boolean) => {
      dispatch({ type: 'SET_LOADING', payload: loading });
    }, []),

    setError: useCallback((error: string | null) => {
      dispatch({ type: 'SET_ERROR', payload: error });
    }, []),

    resetState: useCallback(() => {
      dispatch({ type: 'RESET_STATE' });
    }, []),

    // New collaboration action creators
    setVersionHistory: useCallback((versions: VersionHistoryEntry[]) => {
      dispatch({ type: 'SET_VERSION_HISTORY', payload: versions });
    }, []),

    addVersionHistoryEntry: useCallback((version: VersionHistoryEntry) => {
      dispatch({ type: 'ADD_VERSION_HISTORY_ENTRY', payload: version });
    }, []),

    restoreVersion: useCallback((versionId: string, templateSnapshot: Template) => {
      dispatch({ type: 'RESTORE_VERSION', payload: { versionId, templateSnapshot } });
    }, []),

    setCollaborationSessions: useCallback((sessions: CollaborationSession[]) => {
      dispatch({ type: 'SET_COLLABORATION_SESSIONS', payload: sessions });
    }, []),

    updateCollaborationSession: useCallback((sessionId: string, updates: Partial<CollaborationSession>) => {
      dispatch({ type: 'UPDATE_COLLABORATION_SESSION', payload: { sessionId, updates } });
    }, []),

    removeCollaborationSession: useCallback((sessionId: string) => {
      dispatch({ type: 'REMOVE_COLLABORATION_SESSION', payload: sessionId });
    }, []),

    setComments: useCallback((comments: Comment[]) => {
      dispatch({ type: 'SET_COMMENTS', payload: comments });
    }, []),

    addComment: useCallback((comment: Comment) => {
      dispatch({ type: 'ADD_COMMENT', payload: comment });
    }, []),

    updateComment: useCallback((commentId: string, updates: Partial<Comment>) => {
      dispatch({ type: 'UPDATE_COMMENT', payload: { commentId, updates } });
    }, []),

    deleteComment: useCallback((commentId: string) => {
      dispatch({ type: 'DELETE_COMMENT', payload: commentId });
    }, []),

    resolveComment: useCallback((commentId: string, resolvedBy: string) => {
      dispatch({ type: 'RESOLVE_COMMENT', payload: { commentId, resolvedBy } });
    }, []),

    setApprovalWorkflows: useCallback((workflows: ApprovalWorkflow[]) => {
      dispatch({ type: 'SET_APPROVAL_WORKFLOWS', payload: workflows });
    }, []),

    addApprovalWorkflow: useCallback((workflow: ApprovalWorkflow) => {
      dispatch({ type: 'ADD_APPROVAL_WORKFLOW', payload: workflow });
    }, []),

    updateApprovalWorkflow: useCallback((workflowId: string, updates: Partial<ApprovalWorkflow>) => {
      dispatch({ type: 'UPDATE_APPROVAL_WORKFLOW', payload: { workflowId, updates } });
    }, []),

    setConflicts: useCallback((hasConflicts: boolean, conflictingBlocks: string[], conflictDetails: ConflictDetail[]) => {
      dispatch({ type: 'SET_CONFLICTS', payload: { hasConflicts, conflictingBlocks, conflictDetails } });
    }, []),

    resolveConflict: useCallback((blockId: string, resolution: 'local' | 'remote' | 'merge') => {
      dispatch({ type: 'RESOLVE_CONFLICT', payload: { blockId, resolution } });
    }, []),

    updatePermissions: useCallback((templateId: string, permissions: any[]) => {
      dispatch({ type: 'UPDATE_PERMISSIONS', payload: { templateId, permissions } });
    }, []),
  };

  // Auto-save history entries when template changes
  useEffect(() => {
    if (state.currentTemplate && state.isDirty) {
      const historyEntry: TemplateHistoryEntry = {
        id: generateId(),
        timestamp: new Date(),
        action: 'update',
        description: 'Template updated',
        templateSnapshot: { ...state.currentTemplate },
        userId: 'current-user', // This would come from auth context
      };
      
      // Debounce history entries to avoid too many snapshots
      const timeoutId = setTimeout(() => {
        actions.addHistoryEntry(historyEntry);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [state.currentTemplate, state.isDirty, actions]);

  return (
    <TemplateContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </TemplateContext.Provider>
  );
};

// Hook to use template context
export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
};